import { DegradedErrorCategory, TECHNICAL_ERROR_TEXT_PATTERNS, USER_FACING_ERROR_I18N_KEYS } from './constants';
import logger from './logger';
/**
 * 从未知异常中提取可读消息（仅供日志）。
 *
 * @param {unknown} error - 原始异常。
 * @returns {string}
 */
export function extractErrorMessage(error) {
    if (error instanceof Error) {
        return error.message || error.name || 'UnknownError';
    }
    if (typeof error === 'string') {
        return error;
    }
    if (error && typeof error === 'object' && 'message' in error) {
        const { message } = error;
        if (typeof message === 'string' && message) {
            return message;
        }
    }
    try {
        return JSON.stringify(error);
    }
    catch {
        return String(error);
    }
}
/**
 * 判断文本是否包含不应展示给用户的技术细节。
 *
 * @param {string} text - 候选文案。
 * @returns {boolean}
 */
export function isTechnicalErrorText(text) {
    if (!text || !text.trim()) {
        return false;
    }
    return TECHNICAL_ERROR_TEXT_PATTERNS.some(pattern => pattern.test(text));
}
/**
 * 将异常归类为降级类别。
 *
 * @param {unknown} error - 原始异常。
 * @returns {DegradedErrorCategory}
 */
export function classifyDegradedError(error) {
    const message = extractErrorMessage(error).toLowerCase();
    const name = error instanceof Error ? error.name.toLowerCase() : '';
    const status = error && typeof error === 'object' && 'status' in error
        ? Number(error.status)
        : NaN;
    if (status === 401
        || status === 403
        || message.includes('unauthorized')
        || message.includes('forbidden')
        || message.includes('not authorized')) {
        return DegradedErrorCategory.UNAUTHORIZED;
    }
    if (name.includes('timeout')
        || message.includes('timeout')
        || message.includes('timed out')
        || message.includes('etimedout')) {
        return DegradedErrorCategory.TIMEOUT;
    }
    if (name.includes('network')
        || message.includes('network')
        || message.includes('failed to fetch')
        || message.includes('net::')
        || message.includes('econnrefused')
        || message.includes('enotfound')
        || message.includes('offline')) {
        return DegradedErrorCategory.NETWORK;
    }
    return DegradedErrorCategory.GENERIC;
}
/**
 * 按类别映射到用户可见 i18n key。
 *
 * @param {DegradedErrorCategory} category - 降级类别。
 * @returns {string}
 */
export function getUserFacingI18nKey(category) {
    switch (category) {
        case DegradedErrorCategory.NETWORK:
            return USER_FACING_ERROR_I18N_KEYS.NETWORK;
        case DegradedErrorCategory.TIMEOUT:
            return USER_FACING_ERROR_I18N_KEYS.TIMEOUT;
        case DegradedErrorCategory.UNAUTHORIZED:
            return USER_FACING_ERROR_I18N_KEYS.UNAUTHORIZED;
        case DegradedErrorCategory.GENERIC:
        default:
            return USER_FACING_ERROR_I18N_KEYS.GENERIC;
    }
}
/**
 * 将任意异常转换为用户侧友好错误（不含技术细节）。
 *
 * @param {unknown} error - 原始异常。
 * @returns {IUserFacingError}
 */
export function toUserFacingError(error) {
    const category = classifyDegradedError(error);
    return {
        category,
        i18nKey: getUserFacingI18nKey(category)
    };
}
/**
 * 记录完整错误日志，并返回用户侧友好描述。
 * 技术细节只进日志，不返回给 UI。
 *
 * @param {unknown} error - 原始异常。
 * @param {string} [context] - 业务上下文（模块/操作名）。
 * @returns {IUserFacingError}
 */
export function reportDegradedError(error, context) {
    const userFacing = toUserFacingError(error);
    const report = {
        category: userFacing.category,
        context,
        message: extractErrorMessage(error),
        name: error instanceof Error ? error.name : undefined,
        stack: error instanceof Error ? error.stack : undefined
    };
    logger.error('[错误降级]', `context=${report.context || 'unknown'}`, `category=${report.category}`, `name=${report.name || '-'}`, `message=${report.message}`, report.stack || '');
    return userFacing;
}
/**
 * 将可能含技术细节的通知文案降级为友好 i18n key。
 * 若文案本身已是安全内容则原样返回。
 *
 * TODO: 错误提示交互（Toast 样式、聚合、重试动作等）待完善。
 *
 * @param {string | undefined} text - 原始 description / title。
 * @param {string} fallbackI18nKey - 技术文案时的回退 key。
 * @returns {{ safeText?: string; i18nKey?: string }}
 */
export function degradeNotificationText(text, fallbackI18nKey = USER_FACING_ERROR_I18N_KEYS.GENERIC) {
    if (!text) {
        return {};
    }
    if (isTechnicalErrorText(text)) {
        return { i18nKey: fallbackI18nKey };
    }
    return { safeText: text };
}
